import { ApiClientError, NetworkError } from '@recipes/api-client';
import type {
  MeasurementSystem,
  CookingLogWriteInput,
  DuplicateRecipeRequest,
  Recipe,
  RecipeListQuery,
  RecipePage,
  RecipeUpdateInput,
  RecipeWriteInput,
  ScalePreviewResponse,
  UpdateProfileRequest,
  UpdateShareRequest,
} from '@recipes/contracts';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { currentLocale } from '@/lib/i18n';
import { isOnline } from '@/lib/hooks/use-online-status';
import { offlineDb } from '@/lib/offline/db';
import { readRecipeWithFallback, type RecipeReadResult } from '@/lib/offline/hooks';
import { offlineManager } from '@/lib/offline/manager';
import { enqueuePendingAction } from '@/lib/offline/queue';
import { queryKeys } from '@/lib/query-keys';
import { localScalePreview } from './local-scale';

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------

export function useUnits() {
  return useQuery({
    queryKey: queryKeys.units,
    queryFn: () => api.units.list(),
    staleTime: 24 * 60 * 60_000,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.taxonomy.categories,
    queryFn: () => api.taxonomy.categories(),
    staleTime: 60 * 60_000,
  });
}

export function useTags() {
  return useQuery({
    queryKey: queryKeys.taxonomy.tags,
    queryFn: () => api.taxonomy.tags(),
    staleTime: 10 * 60_000,
  });
}

export function useHouseholds() {
  return useQuery({
    queryKey: queryKeys.households.all,
    queryFn: () => api.households.list(),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.users.profile(),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProfileRequest) => api.users.updateProfile(body),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.profile, profile);
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

// ---------------------------------------------------------------------------
// Recipe detail (with offline fallback)
// ---------------------------------------------------------------------------

export function useRecipe(id: string | undefined) {
  return useQuery<RecipeReadResult>({
    queryKey: queryKeys.recipes.detail(id ?? ''),
    queryFn: () => readRecipeWithFallback(id!, () => api.recipes.get(id!)),
    enabled: !!id,
    retry: false,
  });
}

/** Push a fresh Recipe into the detail cache (after mutations). */
export function useSetRecipeCache() {
  const queryClient = useQueryClient();
  return (recipe: Recipe) => {
    queryClient.setQueryData<RecipeReadResult>(queryKeys.recipes.detail(recipe.id), {
      recipe,
      offlineCopy: false,
      savedAt: null,
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all, refetchType: 'none' });
    // Keep an existing offline copy in sync.
    void offlineDb.offlineRecipes.get(recipe.id).then((entry) => {
      if (entry) void offlineDb.offlineRecipes.put({ ...entry, recipe });
    });
  };
}

// ---------------------------------------------------------------------------
// Library list (infinite) with offline fallback
// ---------------------------------------------------------------------------

export interface RecipePageResult extends RecipePage {
  offlineCopy: boolean;
}

async function offlineListPage(params: Partial<RecipeListQuery>): Promise<RecipePageResult> {
  const entries = await offlineManager.list();
  const q = params.q?.trim().toLowerCase();
  const items = entries
    .map((e) => e.recipe)
    .filter((r) => (params.favorite ? r.isFavorite : true))
    .filter((r) =>
      params.baseRecipe === undefined ? true : (r.isBaseRecipe ?? false) === params.baseRecipe,
    )
    .filter((r) =>
      q
        ? r.title.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q)
        : true,
    )
    .map((r) => ({
      id: r.id,
      title: r.title,
      description: r.description,
      coverThumbnailUrl: r.cover?.thumbnailUrl ?? r.cover?.url ?? null,
      totalTimeMinutes: r.totalTimeMinutes,
      difficulty: r.difficulty,
      servings: r.servings,
      rating: r.rating,
      isFavorite: r.isFavorite,
      isBaseRecipe: r.isBaseRecipe,
      origin: r.origin,
      status: r.status,
      visibility: r.visibility,
      tags: r.tags,
      categories: r.categories,
      cuisine: r.cuisine,
      mealType: r.mealType,
      sourceDomain: r.source?.domain ?? null,
      lastCookedAt: r.lastCookedAt,
      updatedAt: r.updatedAt,
      createdAt: r.createdAt,
    }));
  return { items, nextCursor: null, total: items.length, offlineCopy: true };
}

export function useRecipeList(params: Partial<RecipeListQuery>, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.recipes.list(params),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<RecipePageResult> => {
      if (!isOnline() && !params.trashed) return offlineListPage(params);
      try {
        const page = await api.recipes.list({ ...params, cursor: pageParam });
        return { ...page, offlineCopy: false };
      } catch (error) {
        if (error instanceof NetworkError && !params.trashed) return offlineListPage(params);
        throw error;
      }
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Scaling preview (debounce happens in the component)
// ---------------------------------------------------------------------------

export function useScalePreview(
  recipe: Recipe | undefined,
  servings: string,
  /** The measurement system to show the quantities in; null = as the recipe was written. */
  system: MeasurementSystem | null,
) {
  const id = recipe?.id ?? '';
  const convertUnits = system !== null;
  const isOriginal = recipe !== undefined && servings === recipe.servings && !convertUnits;
  return useQuery<ScalePreviewResponse>({
    queryKey: queryKeys.recipes.scale(id, servings, system ?? false),
    queryFn: async () => {
      if (!recipe) throw new Error('no recipe');
      if (!isOnline()) return localScalePreview(recipe, servings, currentLocale());
      try {
        return await api.recipes.scalePreview(id, {
          servings,
          convertUnits,
          ...(system ? { system } : {}),
        });
      } catch (error) {
        if (error instanceof NetworkError)
          return localScalePreview(recipe, servings, currentLocale());
        throw error;
      }
    },
    enabled: !!recipe && !isOriginal && Number(servings) > 0,
    staleTime: 5 * 60_000,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateRecipe() {
  const setCache = useSetRecipeCache();
  return useMutation({
    mutationFn: ({ input, idempotencyKey }: { input: RecipeWriteInput; idempotencyKey: string }) =>
      api.recipes.create(input, idempotencyKey),
    onSuccess: (recipe) => setCache(recipe),
  });
}

export function useUpdateRecipe(
  options?: UseMutationOptions<Recipe, unknown, { id: string; input: RecipeUpdateInput }>,
) {
  const setCache = useSetRecipeCache();
  return useMutation({
    ...options,
    mutationFn: ({ id, input }: { id: string; input: RecipeUpdateInput }) =>
      api.recipes.update(id, input),
    onSuccess: (recipe, variables, context, mutation) => {
      setCache(recipe);
      options?.onSuccess?.(recipe, variables, context, mutation);
    },
  });
}

/** Favorite toggle with optimistic cache update; queued when offline. */
export function useToggleFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, favorite }: { id: string; favorite: boolean }) => {
      if (!isOnline()) {
        await enqueuePendingAction('favorite', id, { favorite });
        return null;
      }
      try {
        return await api.recipes.setFavorite(id, favorite);
      } catch (error) {
        if (error instanceof NetworkError) {
          await enqueuePendingAction('favorite', id, { favorite });
          return null;
        }
        throw error;
      }
    },
    onMutate: async ({ id, favorite }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.recipes.all });
      const detailKey = queryKeys.recipes.detail(id);
      const previousDetail = queryClient.getQueryData<RecipeReadResult>(detailKey);
      if (previousDetail)
        queryClient.setQueryData<RecipeReadResult>(detailKey, {
          ...previousDetail,
          recipe: { ...previousDetail.recipe, isFavorite: favorite },
        });
      queryClient.setQueriesData<{ pages: RecipePageResult[]; pageParams: unknown[] }>(
        { queryKey: ['recipes', 'list'] },
        (data) =>
          data
            ? {
                ...data,
                pages: data.pages.map((p) => ({
                  ...p,
                  items: p.items.map((i) => (i.id === id ? { ...i, isFavorite: favorite } : i)),
                })),
              }
            : data,
      );
      void offlineDb.offlineRecipes.get(id).then((entry) => {
        if (entry)
          void offlineDb.offlineRecipes.put({
            ...entry,
            recipe: { ...entry.recipe, isFavorite: favorite },
          });
      });
      return { previousDetail };
    },
    onError: (_error, { id }, context) => {
      if (context?.previousDetail)
        queryClient.setQueryData(queryKeys.recipes.detail(id), context.previousDetail);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['recipes', 'list'], refetchType: 'none' });
    },
  });
}

export function useSetRating() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, stars }: { id: string; stars: number | null }) => {
      if (!isOnline()) {
        await enqueuePendingAction('rating', id, { stars });
        return null;
      }
      try {
        return await api.ratings.set(id, { stars });
      } catch (error) {
        if (error instanceof NetworkError) {
          await enqueuePendingAction('rating', id, { stars });
          return null;
        }
        throw error;
      }
    },
    onMutate: ({ id, stars }) => {
      const key = queryKeys.recipes.detail(id);
      const previous = queryClient.getQueryData<RecipeReadResult>(key);
      if (previous)
        queryClient.setQueryData<RecipeReadResult>(key, {
          ...previous,
          recipe: { ...previous.recipe, rating: stars },
        });
      return { previous };
    },
    onError: (_e, { id }, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKeys.recipes.detail(id), context.previous);
    },
    onSettled: (_d, _e, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.rating(id) });
      void queryClient.invalidateQueries({ queryKey: ['recipes', 'list'], refetchType: 'none' });
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.recipes.remove(id),
    onSuccess: (_r, id) => {
      queryClient.removeQueries({ queryKey: queryKeys.recipes.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
    },
  });
}

export function useRestoreRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.recipes.restore(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all }),
  });
}

export function usePurgeRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.recipes.purge(id),
    onSuccess: (_r, id) => {
      queryClient.removeQueries({ queryKey: queryKeys.recipes.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      void offlineManager.remove(id);
    },
  });
}

export function useDuplicateRecipe() {
  const queryClient = useQueryClient();
  const setCache = useSetRecipeCache();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DuplicateRecipeRequest }) =>
      api.recipes.duplicate(id, body),
    onSuccess: (recipe, { id }) => {
      setCache(recipe);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.variations(id) });
    },
  });
}

export function useVariations(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.recipes.variations(id ?? ''),
    queryFn: () => api.recipes.variations(id!),
    enabled: !!id && enabled,
  });
}

export function useShareSettings(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.recipes.share(id ?? ''),
    queryFn: () => api.recipes.share(id!),
    enabled: !!id && enabled,
  });
}

export function useUpdateShare(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateShareRequest) => api.recipes.updateShare(id, body),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.recipes.share(id), settings);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.detail(id) });
    },
  });
}

export function useRevisions(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.recipes.revisions(id ?? ''),
    queryFn: () => api.recipes.revisions(id!),
    enabled: !!id,
  });
}

export function useRecipeCookingLog(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.recipes.cookingLog(id ?? ''),
    queryFn: () => api.ratings.recipeCookingLog(id!, { limit: 20 }),
    enabled: !!id && enabled,
    retry: false,
  });
}

/** Cooking log entry; queued when offline (spec 15). Returns null when queued. */
export function useAddCookingLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: CookingLogWriteInput }) => {
      if (!isOnline()) {
        await enqueuePendingAction('cooking-log', id, body);
        return null;
      }
      try {
        return await api.ratings.addCookingLog(id, body);
      } catch (error) {
        if (error instanceof NetworkError) {
          await enqueuePendingAction('cooking-log', id, body);
          return null;
        }
        throw error;
      }
    },
    onSuccess: (_entry, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.cookingLog(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cookingLog });
    },
  });
}

export function isFeatureDisabled(error: unknown): boolean {
  return error instanceof ApiClientError && error.code === 'FEATURE_DISABLED';
}
